<template>
  <div v-if="suggestion" class="flex items-start gap-3 p-3 bg-brand-subtle border border-brand-outline rounded-md">
    <div class="flex-1 min-w-0">
      <div class="text-sm text-text-default">
        {{ $t('messaging.scheduleSuggestion.offer', {
          question: suggestion.sampleText,
          days: suggestion.distinctDayCount,
          time: suggestion.suggestedTime,
        }) }}
      </div>
      <!-- The refusal, in words. Accept can be turned down by the rooftop gate or by the task cap, and
           until now both looked exactly like a dead button. -->
      <div v-if="error" class="text-sm text-text-error mt-1" role="alert">{{ error }}</div>
      <div class="flex items-center gap-2 mt-2">
        <button
          class="text-xs px-2 py-1 rounded outline outline-1 outline-brand-outline text-text-link"
          :disabled="processing"
          @click="accept"
        >{{ $t('messaging.scheduleSuggestion.accept') }}</button>
        <button
          class="text-xs px-2 py-1 rounded text-text-subtle"
          :disabled="processing"
          @click="dismiss"
        >{{ $t('messaging.scheduleSuggestion.dismiss') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';

export default {
  name: 'ScheduleSuggestionChip',
  props: {
    // The store the surrounding surface is about. The API honours it only when the caller is associated
    // with it, and falls back to their first association when it is absent — so leaving this off is safe,
    // it just costs a multi-store user the right rooftop.
    dealerId: {
      type: String,
      default: '',
    },
  },
  emits: ['created', 'dismissed'],
  data() {
    return {
      suggestion: null,
      processing: false,
      error: '',
    };
  },
  mounted() {
    this.loadSuggestion();
  },
  computed: {
    ...mapStores(useMainStore),
  },
  methods: {
    // The reader's own zone. The API reads their asking hour in it — questions are stored in UTC, and
    // "you ask this every morning" is a claim about their clock — and persists it on the created task, so
    // an accepted offer no longer fires on Eastern time for a store that is not on Eastern time.
    resolvedTimeZone() {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      } catch {
        return '';
      }
    },
    loadSuggestion() {
      const self = this;
      this.mainStore.GetScheduleSuggestion(this.dealerId, this.resolvedTimeZone()).then((response) => {
        self.suggestion = response || null;
      }, () => {
        self.suggestion = null;
      });
    },
    accept() {
      if (this.processing || !this.suggestion) return;
      const self = this;
      const s = this.suggestion;
      this.processing = true;
      this.error = '';
      // No name: the API derives one from the question. Sending "Daily recap" from here is what gave a
      // user who accepted twice two identically-named tasks.
      this.mainStore.AcceptScheduleSuggestion({
        prompt: s.sampleText,
        time: s.suggestedTime,
        cadence: s.suggestedCadence,
        normalizedQuestion: s.normalizedQuestion,
        dealerId: this.dealerId || undefined,
        timeZone: this.resolvedTimeZone() || undefined,
      }).then((task) => {
        self.processing = false;
        self.suggestion = null;
        self.$emit('created', task);
      }, (reason) => {
        self.processing = false;
        // The offer stays on screen: the refusal is usually actionable ("delete one first"), and clearing
        // the chip would leave the reader with an explanation for something no longer in front of them.
        self.error = reason || self.$t('messaging.scheduleSuggestion.failed');
      });
    },
    dismiss() {
      if (this.processing || !this.suggestion) return;
      const self = this;
      const key = this.suggestion.normalizedQuestion;
      this.processing = true;
      this.error = '';
      this.mainStore.DismissScheduleSuggestion(key).then(() => {
        self.processing = false;
        self.suggestion = null;
        self.$emit('dismissed', key);
      }, () => {
        self.processing = false;
        self.suggestion = null;
      });
    },
  },
};
</script>
